import { DomainContext } from '../../changes/DomainContext';
import { RoiId, StructureSetId } from '../../changes/ids';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import { RoiInterpretedType } from '../../dicom/dicomstructureset';
import {
    ApprovedStructureError,
    LockedStructureError,
    StaleReferenceError,
} from '../../errors/DomainError';
import { Image3D } from '../../patient/image3d';
import { Patient } from '../../patient/patient';
import { Roi } from '../../patient/roi';
import { StructureSet } from '../../patient/structureset';
import { DicomProcessor } from '../dicomProcessor';
import { RoiRef } from './RoiService';
import { domainTrace } from '../../diagnostics/domainTrace';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '../../ServiceContainer';

export interface StructureSetRef {
    readonly id: StructureSetId;
}

export class StructureSetService {
    constructor(
        private readonly context: DomainContext,
        private readonly patient: Patient
    ) {}

    /**
     * Creates an empty {@link StructureSet} bound to the given image and
     * adds it to the patient aggregate. The SS is wired to its source
     * image at construction (via the StructureSet constructor's third
     * argument), so no post-hoc connectObjects() pass is needed.
     *
     * The freshly authored SS is added straight to the aggregate without
     * being recorded in any {@link DicomProcessor}'s processed-SOP-UID set.
     * Re-importing its SOP Instance UID later (e.g. after export round-trips
     * it back through DICOM) is handled idempotently by the ingest dedup,
     * which marks-and-skips an object already present in the aggregate
     * rather than throwing.
     */
    createEmpty(image: Image3D): StructureSet {
        domainTrace.service('StructureSetService.createEmpty', {
            imageUid: image.dicomUid,
        });
        return this.context.tracker.batch(() => {
            const dicomStructureSet =
                DicomProcessor.createEmptyDefaultDicomStructureSet(
                    this.patient,
                    image
                );
            const ss = new StructureSet(
                dicomStructureSet,
                dicomStructureSet.frameOfReferenceUid,
                image
            );
            // Mirror DicomProcessor.addNewStructureSet: keep ROI approval
            // status in sync with the SS. No-op for a freshly empty SS
            // (no ROIs yet), but the symmetry is intentional.
            const status = ss.approvalStatus;
            for (const roi of ss.roiArray) {
                roi.approvalStatus = status;
            }
            this.patient.addStructureSet(ss);
            return ss;
        });
    }

    /**
     * Records that the structure set was exported/uploaded. Tracked so
     * version-cached read models (export dialog isNew/isExported flags)
     * invalidate immediately.
     */
    markExported(ref: StructureSetRef): void {
        domainTrace.service('StructureSetService.markExported', {
            id: ref.id,
        });
        this.context.tracker.command(() => this.resolve(ref).setExported());
    }

    /**
     * Sets visibility on every ROI of the structure set in one command, so
     * a bulk show/hide-all emits a single coalesced changeset and bumps the
     * store version (version-keyed projections stay fresh). Visibility is
     * presentation state, editable even on an approved set.
     */
    setVisibility(ref: StructureSetRef, visible: boolean): void {
        domainTrace.service('StructureSetService.setVisibility', {
            id: ref.id,
            visible,
        });
        this.context.tracker.command(() => {
            for (const roi of this.resolve(ref).rois) {
                roi.setVisibility(visible);
            }
        });
    }

    /**
     * Sets contour thickness on every ROI of the structure set in one
     * command (see {@link setVisibility}). Contour thickness is presentation
     * state, editable even on an approved set.
     */
    setContourThickness(ref: StructureSetRef, thickness: number): void {
        domainTrace.service('StructureSetService.setContourThickness', {
            id: ref.id,
            thickness,
        });
        this.context.tracker.command(() => {
            for (const roi of this.resolve(ref).rois) {
                roi.setContourThickness(thickness);
            }
        });
    }

    setDescription(ref: StructureSetRef, description: string): void {
        domainTrace.service('StructureSetService.setDescription', {
            id: ref.id,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setDescription(description)
        );
    }

    approveAll(ref: StructureSetRef, reviewer: string): void {
        domainTrace.service('StructureSetService.approveAll', { id: ref.id });
        this.context.tracker.batch(() => {
            const ss = this.resolve(ref);
            ss.setApprovalStatus(DICOMApprovalStatus.APPROVED, reviewer, false);
            for (const roi of ss.rois) {
                if (!roi.isApproved) {
                    roi.approve(reviewer);
                }
            }
            // setApprovalStatus does not call markChanged — mark the SS
            // explicitly so it appears in the coalesced changeset.
            this.context.tracker.markChanged(ss);
        });
    }

    createRoi(
        ref: StructureSetRef,
        interpretedType: RoiInterpretedType | undefined,
        name: string
    ): Roi {
        domainTrace.service('StructureSetService.createRoi', {
            id: ref.id,
            interpretedType,
        });
        return this.context.tracker.command(() => {
            const ss = this.resolve(ref);
            // StructureSet.createRoi wraps the DicomRoi and keeps the
            // rois[] collection in sync; it inherits the SS tracker.
            const roi = ss.createRoi(interpretedType, name);
            this.context.tracker.markChanged(roi, 'created');
            this.context.tracker.markChanged(ss);
            return roi;
        });
    }

    /**
     * Forgets the undo history and the captured original mask of an ROI that
     * is gone, which would otherwise stay in memory for the rest of the
     * session.
     *
     * Both delete paths ({@link removeRoi} for single-ROI, {@link removeRois}
     * for bulk) dispose through it.
     */
    static forgetRoiSnapshots(roiId: RoiId): void {
        getContourUndoService().clearRoi(roiId);
        getRoiBaselineStore().clearRoi(roiId);
    }

    /**
     * Removes one validated ROI and drops its session snapshots.
     * StructureSet.deleteRoi removes it from both the DicomStructureSet and
     * the rois[] wrapper collection. The caller owns the guards and the
     * enclosing command()/batch().
     */
    private deleteRoiFrom(ss: StructureSet, roi: Roi): void {
        ss.deleteRoi(roi.id);
        this.context.tracker.markChanged(roi, 'deleted');
        this.context.tracker.markChanged(ss);
        StructureSetService.forgetRoiSnapshots(roi.id);
    }

    /**
     * Forgets the session snapshots of EVERY ROI a structure set holds. The
     * per-ROI disposal above covers structures deleted one at a time, but a
     * whole-set delete removes them all at once without ever passing through
     * {@link removeRoi} / {@link removeRois}, leaving snapshots of structures
     * that no longer exist.
     *
     * Exposed as a static so the no-session delete path — which has no
     * service instance to call, see `useDeleteDataset` — disposes through the
     * same code rather than reaching into the stores itself.
     */
    static forgetStructureSetSnapshots(ss: StructureSet): void {
        for (const roi of ss.rois) {
            StructureSetService.forgetRoiSnapshots(roi.id);
        }
    }

    /**
     * Removes an entire structure set from the patient aggregate, cascading
     * the unlink to referencing plans/doses, and disposes the session
     * snapshots of every ROI it held (see
     * {@link forgetStructureSetSnapshots}).
     *
     * Runs in a batch(): {@link Patient.removeStructureSet} marks the set,
     * itself, and every unlinked plan/dose, so one coalesced changeset
     * rebuilds the projections once. The snapshot disposal runs only after
     * the removal has actually happened, so an aborted delete costs the user
     * no undo depth.
     */
    remove(ref: StructureSetRef): void {
        domainTrace.service('StructureSetService.remove', { id: ref.id });
        this.context.tracker.batch(() => {
            const ss = this.resolve(ref);
            this.patient.removeStructureSet(ss);
            StructureSetService.forgetStructureSetSnapshots(ss);
        });
    }

    removeRoi(ref: RoiRef): void {
        domainTrace.service('StructureSetService.removeRoi', { id: ref.id });
        this.context.tracker.command(() => {
            for (const ss of this.patient.structureSets) {
                const roi = ss.getRoiById(ref.id);
                if (!roi) {
                    continue;
                }
                if (ss.isApproved) {
                    throw new ApprovedStructureError(
                        `Cannot delete structures from approved structure ` +
                            `set '${ss.structureSetLabel}'.`
                    );
                }
                if (roi.contourEditBlocker === 'approval') {
                    throw new ApprovedStructureError(
                        `Cannot delete approved structure '${roi.name}'.`
                    );
                }
                if (roi.contourEditBlocker === 'lock') {
                    throw new LockedStructureError(
                        `Cannot delete locked structure '${roi.name}'. ` +
                            `Unlock it first.`
                    );
                }
                this.deleteRoiFrom(ss, roi);
                return;
            }
            throw new StaleReferenceError(
                `Roi ${ref.id} no longer exists. It may have been deleted.`
            );
        });
    }

    /**
     * Deletes several ROIs of one structure set atomically. Every target is
     * validated before any mutation — the set must be unapproved and no
     * target may carry a contour-edit blocker — so an invalid batch aborts
     * with the ground truth untouched. The deletions run in one
     * batch() so a single coalesced changeset rebuilds the engine and
     * projections once.
     */
    removeRois(ref: StructureSetRef, roiIds: readonly RoiId[]): void {
        domainTrace.service('StructureSetService.removeRois', {
            id: ref.id,
            count: roiIds.length,
        });
        const ss = this.resolve(ref);
        if (ss.isApproved) {
            throw new ApprovedStructureError(
                `Cannot delete structures from approved structure set ` +
                    `'${ss.structureSetLabel}'.`
            );
        }

        const targets: Roi[] = [];
        let missing = 0;
        const blocked: string[] = [];
        const lockedNames: string[] = [];
        for (const roiId of roiIds) {
            const roi = ss.getRoiById(roiId);
            if (!roi) {
                missing++;
                continue;
            }
            switch (roi.contourEditBlocker) {
                case 'approval':
                    blocked.push(roi.name);
                    break;
                case 'lock':
                    lockedNames.push(roi.name);
                    break;
                default:
                    targets.push(roi);
            }
        }
        if (missing > 0) {
            throw new StaleReferenceError(
                `${missing} of the structures no longer exist in structure ` +
                    `set '${ss.structureSetLabel}'.`
            );
        }
        if (blocked.length > 0) {
            throw new ApprovedStructureError(
                `Cannot delete approved structures: ${blocked.join(', ')}.`
            );
        }
        if (lockedNames.length > 0) {
            throw new LockedStructureError(
                `Cannot delete locked structures: ${lockedNames.join(', ')}.`
            );
        }

        this.context.tracker.batch(() => {
            for (const roi of targets) {
                this.deleteRoiFrom(ss, roi);
            }
        });
    }

    private resolve(ref: StructureSetRef): StructureSet {
        const ss = this.patient.getStructureSetById(ref.id);
        if (!ss) {
            throw new StaleReferenceError(
                `StructureSet ${ref.id} no longer exists.`
            );
        }
        return ss;
    }
}
